import { memo, useCallback, useState } from 'react';
import { Button, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { studioDescriptionKey } from '../../utils/catalogText';
import StudioIcon from './StudioIcon';

/**
 * Lists the studios the member does not have yet, each with a Grant button.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object[]} props.studios Grantable studios (not yet granted).
 * @param {string} props.memberName
 * @param {() => void} props.onClose
 * @param {(studioId: string) => Promise<boolean>} props.onGrant Resolves `true` when granted (the modal closes).
 */
const GrantAccessModal = ({ isOpen, studios, memberName, onClose, onGrant }) => {
  const { t } = useTranslation('people-access');
  const [pendingId, setPendingId] = useState(null);

  const handleGrant = useCallback(
    async (studioId) => {
      setPendingId(studioId);
      const ok = await onGrant(studioId);
      setPendingId(null);
      if (ok) onClose();
    },
    [onGrant, onClose],
  );

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      useBodyPortal
      title={t('studios.grantModal.title')}
      subtitle={t('studios.grantModal.subtitle', { name: memberName })}
      closeAriaLabel={t('studios.grantModal.close')}
      closeDisabled={pendingId !== null}
      maxWidthClassName="max-w-lg"
    >
      <ul className="flex flex-col gap-2 px-6 py-4">
        {studios.map((studio) => (
          <li key={studio.id} className="flex items-center gap-3 rounded-lg border border-edge p-3">
            <StudioIcon icon={studio.icon} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-content">{studio.name}</p>
              <p className="truncate text-xs text-content-muted">{t(studioDescriptionKey(studio.id), { defaultValue: studio.description })}</p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              aria-label={t('studios.grantModal.grantAria', { studio: studio.name })}
              loading={pendingId === studio.id}
              disabled={pendingId !== null}
              onClick={() => handleGrant(studio.id)}
            >
              {t('studios.grantModal.grant')}
            </Button>
          </li>
        ))}
      </ul>
    </ModalLayout>
  );
};

export default memo(GrantAccessModal);
